import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, Package, Users, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ROIDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [detailed, setDetailed] = useState(null);
  const [rollup, setRollup] = useState(null);
  const [recovered, setRecovered] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);

  const token = localStorage.getItem('token');
  const orgId = localStorage.getItem('current_organization_id');

  useEffect(() => {
    fetchROISummary();
    fetchDetailedMetrics();
    fetchLocationRollup();
    fetchRecovered();
  }, [period]);

  const fetchRecovered = async () => {
    try {
      const res = await fetch(`${API_URL}/api/roi/recovered`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok) {
        setRecovered(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch recovered headline:', err);
    }
  };

  const fetchROISummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/roi/summary?period_days=${period}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      } else {
        setError('Failed to fetch ROI summary');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailedMetrics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/roi/metrics/detailed`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok) {
        const data = await res.json();
        setDetailed(data);
      }
    } catch (err) {
      console.error('Failed to fetch detailed metrics:', err);
    }
  };

  const fetchLocationRollup = async () => {
    try {
      const res = await fetch(`${API_URL}/api/locations/rollup/summary?period_days=${period}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok) {
        const data = await res.json();
        setRollup(data);
      }
    } catch (err) {
      console.error('Failed to fetch location rollup:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const chartData = detailed?.orders_by_day ? Object.entries(detailed.orders_by_day)
    .map(([date, count]) => ({ date, orders: count }))
    .reverse() : [];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">ROI Dashboard</h1>
        <p className="text-gray-600">Track your cost savings and revenue impact</p>
      </div>

      {recovered && (
        <div
          className="mb-6 p-4 rounded-lg border border-emerald-200 bg-emerald-50"
          data-testid="roi-recovered-headline"
        >
          <p className="text-lg font-semibold text-emerald-900">{recovered.headline}</p>
          <p className="text-sm text-emerald-800 mt-1">
            Raise-price opportunity + supplier price moves (estimate — see assumptions).
          </p>
          {recovered.assumptions?.note && (
            <p className="text-xs text-emerald-700 mt-2">{recovered.assumptions.note}</p>
          )}
        </div>
      )}

      {/* Period Selector */}
      <div className="mb-6 flex items-center space-x-4">
        <label className="text-sm font-medium text-gray-700">Period:</label>
        <select aria-label="Period"
          value={period}
          onChange={(e) => setPeriod(Number(e.target.value))}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {summary && (
        <>
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Savings</p>
                  <p className="text-3xl font-bold text-gray-900">{formatCurrency(summary.total_savings)}</p>
                  <p className="text-sm text-green-600 mt-1">ROI: {summary.roi_percentage.toFixed(1)}%</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Commission Saved</p>
                  <p className="text-3xl font-bold text-gray-900">{formatCurrency(summary.commission_savings)}</p>
                  <p className="text-sm text-gray-600 mt-1">From {summary.order_count} direct orders</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Waste Reduction</p>
                  <p className="text-3xl font-bold text-gray-900">{formatCurrency(summary.waste_reduction)}</p>
                  <p className="text-sm text-gray-600 mt-1">From better inventory</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Revenue Captured</p>
                  <p className="text-3xl font-bold text-gray-900">{formatCurrency(summary.total_revenue_captured)}</p>
                  <p className="text-sm text-gray-600 mt-1">Direct orders</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Order Trends */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Order Trends</h3>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="orders" stroke="#3b82f6" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No order data available
                </div>
              )}
            </div>

            {/* Savings Breakdown */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Savings Breakdown</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={[
                  { category: 'Commission', value: summary.commission_savings },
                  { category: 'Waste', value: summary.waste_reduction },
                  { category: 'Labor', value: summary.labor_savings }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="value" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Multi-site Rollup */}
          <div className="bg-white shadow rounded-lg p-6 mb-8" data-testid="site-rollup-panel">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h3 className="text-lg font-semibold">Site Rollup</h3>
                <p className="text-sm text-gray-600 mt-1">Revenue, alerts and stock value across locations.</p>
              </div>
              {rollup && (
                <div className="text-right text-sm text-gray-500" data-testid="site-rollup-period">
                  {new Date(rollup.period_start).toLocaleDateString()} – {new Date(rollup.period_end).toLocaleDateString()}
                </div>
              )}
            </div>

            {rollup ? (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs uppercase text-gray-500">Rollup Revenue</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(rollup.total_revenue)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs uppercase text-gray-500">Orders</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{rollup.order_count}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs uppercase text-gray-500">Open Alerts</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{rollup.open_alerts}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs uppercase text-gray-500">Stock Value</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(rollup.stock_value)}</p>
                  </div>
                </div>

                <div className="overflow-x-auto" data-testid="site-rollup-table">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Orders</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">AOV</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Open alerts</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Stock value</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(rollup.locations || []).map(location => (
                        <tr key={location.location_id} data-testid={`site-rollup-row-${location.location_id}`}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{location.location_name}</td>
                          <td className="px-4 py-3 text-sm text-right">{formatCurrency(location.total_revenue)}</td>
                          <td className="px-4 py-3 text-sm text-right">{location.order_count}</td>
                          <td className="px-4 py-3 text-sm text-right">{formatCurrency(location.average_order_value)}</td>
                          <td className="px-4 py-3 text-sm text-right">{location.open_alerts}</td>
                          <td className="px-4 py-3 text-sm text-right">{formatCurrency(location.stock_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-500 py-6" data-testid="site-rollup-empty">
                No multi-site rollup data available yet.
              </div>
            )}
          </div>

          {/* Summary Table */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Period Summary</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Total Revenue</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">{formatCurrency(summary.total_revenue_captured)}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Commission Savings</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-green-600">{formatCurrency(summary.commission_savings)}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Waste Reduction</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-green-600">{formatCurrency(summary.waste_reduction)}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Labor Savings</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-green-600">{formatCurrency(summary.labor_savings)}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">Total Savings</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-right text-green-600">{formatCurrency(summary.total_savings)}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Platform Cost</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-red-600">{formatCurrency(summary.period_cost)}</td>
                  </tr>
                  <tr className="bg-blue-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">Net ROI</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-right text-blue-600">{summary.roi_percentage.toFixed(1)}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ROIDashboard;
